import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { api, formatErr } from "@/lib/api";

const Shell = ({ eyebrow, title, children }) => (
  <div className="mx-auto grid max-w-md px-4 py-16">
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">{eyebrow}</p>
    <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">{title}</h1>
    <div className="rise mt-8 space-y-4 rounded-3xl bg-[#173742] p-6 sm:p-8">{children}</div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(formatErr(e2)); } finally { setBusy(false); }
  };

  return (
    <Shell eyebrow="Account help" title="Forgot your password?">
      {sent ? (
        <div data-testid="forgot-sent-msg" className="space-y-3 text-center">
          <MailCheck className="mx-auto h-10 w-10 text-[#d4f253]" />
          <p className="font-semibold">Check your inbox</p>
          <p className="text-sm text-slate-400">If <span className="text-white">{email}</span> is registered, we've sent a link to reset your password. It expires in 1 hour.</p>
          <Link to="/login" data-testid="forgot-back-login-link" className="inline-block text-sm font-semibold text-[#d4f253] hover:underline">Back to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-slate-400">Enter your account email and we'll send you a reset link.</p>
          <input data-testid="forgot-email-input" type="email" required className="field" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          {err && <p data-testid="forgot-error" className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">{err}</p>}
          <button data-testid="forgot-submit-btn" disabled={busy} className="btn-lime w-full py-3.5 text-sm">{busy ? "Sending…" : "Send reset link"}</button>
          <p className="text-center text-sm"><Link to="/login" className="text-slate-400 hover:text-[#d4f253]">Remembered it? Log in</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      localStorage.removeItem("isoldit_token");
      toast.success("Password updated — please log in");
      nav("/login");
    } catch (e2) { setErr(formatErr(e2)); } finally { setBusy(false); }
  };

  if (!token) return <Shell eyebrow="Account help" title="Link missing"><p data-testid="reset-missing-token" className="text-sm text-slate-400">This reset link is incomplete. <Link to="/forgot-password" className="font-semibold text-[#d4f253]">Request a new one</Link>.</p></Shell>;

  return (
    <Shell eyebrow="Account help" title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <input data-testid="reset-password-input" type="password" required minLength={6} className="field" placeholder="New password (min 6 chars)" value={pw} onChange={(e) => setPw(e.target.value)} />
        <input data-testid="reset-password-confirm-input" type="password" required minLength={6} className="field" placeholder="Confirm new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
        {err && <p data-testid="reset-error" className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">{err} {err.includes("expired") && <Link to="/forgot-password" className="font-semibold underline">Request new link</Link>}</p>}
        <button data-testid="reset-submit-btn" disabled={busy} className="btn-lime w-full py-3.5 text-sm">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </Shell>
  );
}
